
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ComplianceViolation {
  id: string;
  content_id: string;
  content_type: 'node' | 'comment' | 'profile';
  violation_type: string;
  severity: 'minor' | 'moderate' | 'severe' | 'critical';
  detected_by: 'automated' | 'user_report' | 'admin_review';
  detection_confidence: number;
  violation_data: Record<string, any>;
  status: 'open' | 'investigating' | 'resolved' | 'dismissed';
  assigned_to?: string;
  resolution_notes?: string;
  resolved_at?: string;
  created_at: string;
}

export const useComplianceViolations = () => {
  const [loading, setLoading] = useState(false);
  const [violations, setViolations] = useState<ComplianceViolation[]>([]);
  const { toast } = useToast();

  const fetchComplianceViolations = async (filters?: {
    status?: string;
    severity?: string;
    contentType?: string;
  }) => {
    setLoading(true);
    try {
      let query = supabase
        .from('compliance_violations')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.severity) {
        query = query.eq('severity', filters.severity);
      }
      if (filters?.contentType) {
        query = query.eq('content_type', filters.contentType);
      }

      const { data, error } = await query;

      if (error) throw error;
      setViolations((data || []) as ComplianceViolation[]);
    } catch (error: any) {
      console.error('Error fetching compliance violations:', error);
      toast({
        title: "Error",
        description: "Failed to load compliance violations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateViolationStatus = async (
    violationId: string,
    status: 'open' | 'investigating' | 'resolved' | 'dismissed',
    resolutionNotes?: string
  ) => {
    try {
      const updateData: any = { status };
      if (status === 'resolved' || status === 'dismissed') {
        updateData.resolved_at = new Date().toISOString();
        if (resolutionNotes) {
          updateData.resolution_notes = resolutionNotes;
        }
      }

      const { error } = await supabase
        .from('compliance_violations')
        .update(updateData)
        .eq('id', violationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Violation status updated successfully",
      });

      fetchComplianceViolations();
    } catch (error: any) {
      console.error('Error updating violation status:', error);
      toast({
        title: "Error",
        description: "Failed to update violation status",
        variant: "destructive",
      });
    }
  };

  return {
    loading,
    violations,
    fetchComplianceViolations,
    updateViolationStatus,
  };
};
